// src/pages/dashboard/MessageBoard.jsx
import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

const MessageBoard = () => {
  const [user, setUser] = useState(null);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [replyInputs, setReplyInputs] = useState({});

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) {
        setUser(data.user);
        fetchAllMessages(data.user.id);
      }
    });
  }, []);

  const fetchAllMessages = async (userId) => {
  const [annRes, sentRes, replyRes] = await Promise.all([
    supabase
      .from('user_announcements')
      .select('id, is_read, read_at, announcement:announcement_id(id, title, message, created_at)')
      .eq('user_id', userId),

    supabase
      .from('user_to_admin_messages')
      .select('id, subject, body, created_at')
      .eq('user_id', userId),

    supabase
      .from('message_replies')
      .select('*')
      .order('created_at', { ascending: true })
  ]);

  if (annRes.error || sentRes.error || replyRes.error) {
    toast.error('Error loading messages');
    return;
  }

  // ✅ Auto-mark unread announcements as read
  const unreadAnnouncementIds = annRes.data.filter((a) => !a.is_read).map((a) => a.id);
  if (unreadAnnouncementIds.length > 0) {
    await supabase
      .from('user_announcements')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .in('id', unreadAnnouncementIds);

    window.dispatchEvent(new Event("refresh-unread-count"));
  }

  const repliesByAnnouncement = {};
  const repliesByUserMsg = {};

  for (let r of replyRes.data) {
    if (r.user_announcement_id) {
      if (!repliesByAnnouncement[r.user_announcement_id]) repliesByAnnouncement[r.user_announcement_id] = [];
      repliesByAnnouncement[r.user_announcement_id].push(r);
    }
    if (r.user_to_admin_message_id) {
      if (!repliesByUserMsg[r.user_to_admin_message_id]) repliesByUserMsg[r.user_to_admin_message_id] = [];
      repliesByUserMsg[r.user_to_admin_message_id].push(r);
    }
  }

  const allMessages = [];

  for (let a of annRes.data) {
    allMessages.push({
      id: `announcement-${a.id}`,
      type: 'announcement',
      title: a.announcement.title,
      body: a.announcement.message,
      created_at: a.announcement.created_at,
      is_read: true, // Assume now it's marked
      read_at: a.read_at,
      replies: repliesByAnnouncement[a.id] || [],
    });
  }

  for (let m of sentRes.data) {
    allMessages.push({
      id: `sent-${m.id}`,
      type: 'sent',
      title: m.subject,
      body: m.body,
      created_at: m.created_at,
      replies: repliesByUserMsg[m.id] || [],
    });
  }

  allMessages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filtered = allMessages.filter(
    (msg) =>
      msg.title.toLowerCase().includes(search.toLowerCase()) ||
      msg.body.toLowerCase().includes(search.toLowerCase())
  );

  setFilteredMessages(filtered);
};


  const markAllAsRead = async () => {
    const unread = filteredMessages.filter((m) => m.type === 'announcement' && !m.is_read);
    if (unread.length === 0) return toast('All messages already read');

    const { error } = await supabase
      .from('user_announcements')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .in('id', unread.map((m) => m.id.split('-')[1]));

    if (!error) {
      toast.success('All messages marked as read');
      window.dispatchEvent(new Event("refresh-unread-count")); // ✅ Refresh badge
      fetchAllMessages(user.id);
    }
  };

  const handleSendMessage = async () => {
    if (!subject || !body) return toast.error('Please fill in both fields.');

    const { error } = await supabase.from('user_to_admin_messages').insert({
      user_id: user.id,
      subject,
      body,
    });

    if (error) toast.error('Error sending message.');
    else {
      toast.success('Message sent to admin.');
      setSubject('');
      setBody('');
      fetchAllMessages(user.id);
    }
  };

  const handleReply = async (msg) => {
    const content = replyInputs[msg.id];
    if (!content || !user?.id) return toast.error('Reply cannot be empty');

    const payload = {
      sender_id: user.id,
      body: content,
    };

    const isAnnouncement = msg.type === 'announcement';
    const isUserMessage = msg.type === 'sent';

    if (isAnnouncement) {
      payload.user_announcement_id = msg.id.split('-')[1];
    } else if (isUserMessage) {
      payload.user_to_admin_message_id = msg.id.split('-')[1];
    } else {
      return toast.error('Invalid message type');
    }

    const { error } = await supabase.from('message_replies').insert(payload);

    if (error) {
      console.error('Supabase error:', error);
      toast.error('Failed to send reply');
    } else {
      toast.success('Reply sent');
      setReplyInputs((prev) => ({ ...prev, [msg.id]: '' }));
      window.dispatchEvent(new Event("refresh-unread-count")); // ✅ Refresh badge
      fetchAllMessages(user.id);
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">📬 Message Board</h2>

      <div className="flex flex-col md:flex-row justify-between items-center gap-2 mb-4">
        <input
          type="text"
          placeholder="Search messages..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-1/2 border p-2 rounded"
        />
        <button
          onClick={markAllAsRead}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Mark All as Read
        </button>
      </div>

      {filteredMessages.length === 0 ? (
        <p>No messages found.</p>
      ) : (
        filteredMessages.map((msg) => (
          <div
            key={msg.id}
            className={`border p-4 rounded mb-4 transition hover:shadow-md ${
              msg.is_read === false ? 'bg-indigo-100 border-indigo-300' : 'bg-white'
            }`}
          >
            <h3 className="text-lg font-semibold flex items-center gap-2">
              {msg.type === 'sent' ? '📝 You wrote:' : '📢 Admin Message:'} {msg.title}
            </h3>
            <p className="text-sm text-gray-600">
              {format(new Date(msg.created_at), 'PPpp')}
            </p>
            <p className="mt-2">{msg.body}</p>

            {msg.replies.length > 0 && (
              <div className="mt-4 border-t pt-3">
                <h4 className="text-sm font-semibold mb-2">Replies</h4>
                {msg.replies.map((r) => (
                  <div key={r.id} className="mb-2 text-sm">
                    <p className="text-xs text-gray-500">
                      {r.sender_id === user.id ? '👤 You' : '🛡️ Admin'} • {format(new Date(r.created_at), 'PPpp')}
                    </p>
                    <p>{r.body}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Reply Input */}
            <div className="mt-4">
              <textarea
                rows={2}
                placeholder="Write a reply..."
                value={replyInputs[msg.id] || ''}
                onChange={(e) =>
                  setReplyInputs((prev) => ({ ...prev, [msg.id]: e.target.value }))
                }
                className="w-full border rounded p-2 text-sm"
              />
              <button
                onClick={() => handleReply(msg)}
                className="mt-2 bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
              >
                Send Reply
              </button>
            </div>
          </div>
        ))
      )}

      {/* Send New Message */}
      <div className="border-t pt-4 mt-6">
        <h3 className="text-xl font-semibold mb-2">Send a Message to Admin</h3>
        <input
          type="text"
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full border p-2 rounded mb-2"
        />
        <textarea
          placeholder="Write your message here..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full border p-2 rounded mb-2"
          rows={4}
        ></textarea>
        <button
          onClick={handleSendMessage}
          className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
        >
          Send Message
        </button>
      </div>
    </div>
  );
};

export default MessageBoard;
